import { useState } from 'react';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Typography,
  Button,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { useFormik } from 'formik';
import { useNotify, useRefresh } from 'react-admin';
import { getClientApi } from 'src/data-communication';
import { MInputFile } from 'src/lib';
import { AccountApplicationCategory } from 'src/models';
import * as yup from 'yup';

const clientApi = getClientApi();

interface UploadApplicationDocumentFormValues {
  file?: File;
  type: string;
}

const validationSchema = yup.object().shape({
  file: yup.string().required('Document is required'),
  type: yup.string(),
});

interface UploadApplicationDocumentModalProps {
  applicationId: string;
  accountId: string;
  isOpen: boolean;
  onClose: () => void;
  category: AccountApplicationCategory;
}

export const UploadApplicationDocumentModal = ({
  applicationId,
  accountId,
  isOpen,
  onClose,
  category,
}: UploadApplicationDocumentModalProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number | null>(null);
  const notify = useNotify();
  const refresh = useRefresh();

  const getOptionList = () =>
    category.requestedDocuments.map(doc => ({
      label: doc.label,
      value: doc.value,
    }));

  const formik = useFormik<UploadApplicationDocumentFormValues>({
    initialValues: {
      type: category.requestedDocuments[0]?.value || '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values: UploadApplicationDocumentFormValues) => {
      setIsLoading(true);
      const formData = new FormData();

      if (values.file) {
        formData.append('file', values.file);
        formData.append('size', values.file.size.toString());
        formData.append('originalFilename', values.file.name);
        formData.append('type', values.type);
      }

      try {
        await clientApi.accountApplication.uploadDocument({
          params: {
            id: applicationId,
            accountId,
          },
          body: formData,
          onProgress: (percent: number) => {
            setProgress(percent);
          },
        });
        notify(`Document was successfully uploaded.`, {
          type: 'success',
          autoHideDuration: 7500,
        });
        refresh();
      } catch (error: any) {
        notify(error.message, {
          type: 'error',
        });
      } finally {
        onClose();
        setIsLoading(false);
        setProgress(null);
      }
    },
  });

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth>
      <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Grid container direction='column' spacing={2}>
            {getOptionList().length > 1 ? (
              <Grid item xs={6} md={6}>
                <FormControl margin='dense' variant='standard' fullWidth sx={{ width: '100%' }}>
                  <FormLabel id='type'>Document Type</FormLabel>
                  <RadioGroup
                    name='type'
                    value={formik.values.type}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}>
                    {getOptionList().map(anItem => (
                      <FormControlLabel
                        key={anItem.value}
                        value={anItem.value}
                        control={<Radio disabled={isLoading} />}
                        label={anItem.label}
                      />
                    ))}
                  </RadioGroup>
                  {formik.errors.type && (
                    <Typography variant='caption' color='error'>
                      {formik.errors.type}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            ) : (
              <Grid item xs={6} md={6}>
                <FormControl margin='dense' variant='standard' fullWidth sx={{ width: '100%' }}>
                  <FormLabel id='type'>Document Type</FormLabel>
                  <Typography variant='body1'>{category.requestedDocuments[0]?.label}</Typography>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={6} md={6}>
              <FormControl margin='dense' variant='standard' fullWidth sx={{ width: '100%' }}>
                <FormLabel id='file'>Document</FormLabel>
                <MInputFile
                  error={formik.errors.file}
                  accept={
                    category.name.isGovernmentId ? ['image/png', 'image/jpeg', 'application/pdf'] : ['application/pdf']
                  }
                  file={formik.values.file}
                  onChange={file => formik.setFieldValue('file', file)}
                  percent={progress ?? undefined}
                />
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid item xs={12} container direction='row' justifyContent='flex-end' spacing={2}>
            <Grid item>
              <Button variant='outlined' color='inherit' size='medium' onClick={onClose}>
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button variant='contained' color='primary' size='medium' disabled={isLoading} type='submit'>
                Save
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </form>
    </Dialog>
  );
};
